import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserPlus, Loader2, CheckCircle2, Crown, Users, ArrowRight, ArrowLeft, QrCode, Building2, Lightbulb, MailCheck, Clock, X } from "lucide-react";
import { BrutalButton, BrutalCard } from "@/components/Brutal";
import { useAuth } from "@/context/AuthContext";
import { makeT } from "@/lib/i18n";
import { LANGS, RTL_LANGS } from "@/lib/onboarding";
import api, { formatError, API } from "@/lib/api";
import { toast } from "sonner";

const ORG_TYPES = [
  { v: "huishouden", l: "Huishouden" }, { v: "gezin", l: "Gezin" },
  { v: "studentenhuis", l: "Studentenhuis" }, { v: "vereniging", l: "Vereniging" },
  { v: "instelling", l: "Instelling" }, { v: "bedrijf", l: "Bedrijf / Organisatie (overig)" },
];

export default function Aanmelden() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const initialOrg = params.get("org") || "";
  const refId = params.get("ref") || "";

  const [lang, setLang] = useState(() => localStorage.getItem("slackr_lang") || "nl");
  const t = makeT(lang);
  const rtl = RTL_LANGS.includes(lang);
  const pickLang = (c) => { setLang(c); localStorage.setItem("slackr_lang", c); };

  const [step, setStep] = useState(initialOrg ? "join" : "first");
  const [busy, setBusy] = useState(false);
  const [acc, setAcc] = useState({ name: "", email: "", password: "" });
  const [org, setOrg] = useState({ name: "", type: "huishouden" });
  const [code, setCode] = useState("");
  const [joinId, setJoinId] = useState(initialOrg);
  const [joinTarget, setJoinTarget] = useState(null);
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });

  const [pendingReq, setPendingReq] = useState(null);
  const [checkingPending, setCheckingPending] = useState(!!user);

  useEffect(() => { if (initialOrg && step === "join" && !joinTarget) validateOrg(initialOrg); }, [step]);

  // Ingelogde gebruiker zonder organisatie: toon "wacht op goedkeuring" bij een openstaand aansluitverzoek.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) { setCheckingPending(false); return; }
      try {
        const { data } = await api.get("/my/join-requests");
        if (!cancelled && Array.isArray(data) && data.length) setPendingReq(data[0]);
      } catch { /* geen openstaand verzoek */ }
      finally { if (!cancelled) setCheckingPending(false); }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const withdrawRequest = async () => {
    if (!pendingReq) return;
    setBusy(true);
    try {
      await api.delete(`/my/join-requests/${pendingReq.id}`);
      toast.success(t("Verzoek ingetrokken"));
      setPendingReq(null);
      setStep("first");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Bestaande (ingelogde) gebruiker maakt een extra organisatie aan.
  const createOrg = async () => {
    if (!org.name.trim()) return toast.error(t("Geef je organisatie een naam"));
    setBusy(true);
    try {
      const { data } = await api.post("/organizations", { name: org.name.trim(), type: org.type });
      localStorage.setItem("slackr_org", data.org_id);
      toast.success(t("Organisatie aangemaakt 🎉"));
      window.location.href = "/vandaag";
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); setBusy(false); }
  };

  // Nieuwe gebruiker maakt organisatie aan + wordt beheerder → e-mail verifiëren.
  const registerOwner = async () => {
    if (!org.name.trim()) return toast.error(t("Geef je organisatie een naam"));
    if (!acc.name.trim() || !acc.email.trim() || acc.password.length < 6) return toast.error(t("Vul naam, e-mail en een wachtwoord (min. 6 tekens) in"));
    setBusy(true);
    try {
      await api.post("/auth/register-owner", {
        name: acc.name.trim(), email: acc.email.trim(), password: acc.password,
        org_name: org.name.trim(), org_type: org.type, base_url: window.location.origin,
        language: lang,
      });
      toast.success(t("Account aangemaakt! Check je e-mail voor de code 📬"));
      setStep("verify");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.trim().length < 6) return toast.error(t("Vul de 6-cijferige code in"));
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-email", { email: acc.email.trim(), code: code.trim() });
      localStorage.setItem("doenies_token", data.session_token);
      if (data.org_id) localStorage.setItem("slackr_org", data.org_id);
      toast.success(t("Gelukt! Welkom bij Slackr 🎉"));
      window.location.href = "/vandaag";
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); setBusy(false); }
  };

  const resend = async () => {
    try { await api.post("/auth/resend-code", { email: acc.email.trim(), base_url: window.location.origin }); toast.success(t("Nieuwe code verstuurd 📬")); }
    catch { toast.error(t("Kon code niet versturen")); }
  };

  const validateOrg = async (id) => {
    const v = (id ?? joinId).trim();
    if (!v) return toast.error(t("Voer een organisatie-ID in"));
    setBusy(true);
    try {
      const { data } = await api.get(`/organizations/${v}/exists`);
      if (!data.exists) { toast.error(t("Geen organisatie gevonden met dit ID")); setJoinTarget(null); }
      else setJoinTarget({ id: v, name: data.name, type: data.type });
    } catch { toast.error(t("Kon organisatie niet controleren")); }
    finally { setBusy(false); }
  };

  const requestJoin = async () => {
    setBusy(true);
    try {
      const body = user ? {} : { name: contact.name, email: contact.email, phone: contact.phone };
      if (refId) body.invited_by = refId;
      await api.post(`/organizations/${joinTarget.id}/join-request`, body);
      toast.success(t("Aanvraag verstuurd"));
      setStep("waiting");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const joinUrl = joinTarget ? `${window.location.origin}/aanmelden?org=${joinTarget.id}` : "";
  const joinQr = joinTarget ? `${API}/app-qr?base_url=${encodeURIComponent(window.location.origin)}&path=${encodeURIComponent(`/aanmelden?org=${joinTarget.id}`)}` : "";

  return (
    <div dir={rtl ? "rtl" : "ltr"} className="min-h-screen bg-brand-cream flex items-center justify-center p-5" data-testid="aanmelden-page">
      <div className="w-full max-w-md">
        <div className="flex flex-wrap gap-1.5 justify-center mb-4" data-testid="aanmelden-langs">
          {LANGS.map((l) => (
            <button key={l.code} data-testid={`aanmelden-lang-${l.code}`} onClick={() => pickLang(l.code)}
              className={`rounded-full px-2.5 py-1 text-xs font-bold brutal-sm ${lang === l.code ? "bg-brand-violet text-white" : "bg-white"}`}>{l.flag} {l.label}</button>
          ))}
        </div>
        <div className="flex flex-col items-center mb-1">
          <img src="/icon-192.png" alt="Slackr" className="w-16 h-16 rounded-2xl brutal-sm mb-3" data-testid="onb-logo" />
          <h1 className="font-head font-black text-4xl text-center">Slackr</h1>
        </div>
        <p className="text-center text-brand-ink/60 font-semibold mb-6">{t("Gratis proberen — 30 dagen")}</p>
        <BrutalCard>
          {checkingPending && (
            <div className="text-center py-8" data-testid="onb-pending-loading">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-brand-violet" />
            </div>
          )}

          {!checkingPending && pendingReq && (
            <div className="text-center py-4 space-y-3" data-testid="onb-pending">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-brand-yellow brutal-sm flex items-center justify-center">
                <Clock className="w-8 h-8" strokeWidth={2.5} />
              </div>
              <h2 className="font-head font-black text-xl">{t("Wacht op goedkeuring")}</h2>
              <p className="text-brand-ink/70 font-semibold -mt-1">
                {t("Je aanvraag om aan te sluiten bij")}{" "}
                <b data-testid="onb-pending-org">{pendingReq.org_name || t("een organisatie")}</b>{" "}
                {t("is verstuurd.")}
              </p>
              <div className="brutal-sm rounded-xl bg-brand-cream p-3 text-sm font-medium text-brand-ink/70" data-testid="onb-pending-note">
                {t("Een beheerder bekijkt je aanmelding. Zodra je bent goedgekeurd, kun je meteen aan de slag. Je hoeft niets nieuws aan te maken.")}
              </div>
              <button data-testid="onb-pending-withdraw" onClick={withdrawRequest} disabled={busy}
                className="w-full brutal-sm brutal-press rounded-xl p-3 flex items-center justify-center gap-2 font-head font-bold bg-white text-brand-ink/70">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <X className="w-5 h-5" strokeWidth={2.5} />}
                {t("Verzoek intrekken")}
              </button>
            </div>
          )}

          {!checkingPending && !pendingReq && step === "first" && (
            <div className="space-y-3" data-testid="onb-first">
              <p className="font-head font-bold text-lg">{t("Ben je de eerste van je organisatie op Slackr?")}</p>
              <p className="text-sm text-brand-ink/60 -mt-1">{t("Denk aan je huishouden, studentenhuis, vereniging of instelling.")}</p>
              <button data-testid="onb-first-yes" onClick={() => setStep("create")} className="w-full brutal-sm brutal-press rounded-xl p-4 flex items-center gap-3 text-start bg-brand-yellow">
                <Crown className="w-7 h-7 shrink-0" strokeWidth={2.5} />
                <div><p className="font-head font-bold">{t("Ja, ik ben de eerste")}</p><p className="text-xs text-brand-ink/70">{t("Ik maak een nieuwe organisatie aan & word beheerder")}</p></div>
              </button>
              <button data-testid="onb-first-no" onClick={() => setStep("join")} className="w-full brutal-sm brutal-press rounded-xl p-4 flex items-center gap-3 text-start bg-white">
                <Users className="w-7 h-7 shrink-0" strokeWidth={2.5} />
                <div><p className="font-head font-bold">{t("Nee, ik sluit aan")}</p><p className="text-xs text-brand-ink/60">{t("Ik heb een organisatie-ID of QR-code")}</p></div>
              </button>
              {!user && <p className="text-center text-sm text-brand-ink/50 pt-1">{t("Al een account?")} <button type="button" onClick={() => navigate("/login")} className="underline font-bold">{t("Inloggen")}</button></p>}
            </div>
          )}

          {!checkingPending && !pendingReq && step === "create" && (
            <div className="space-y-3" data-testid="onb-create">
              <button onClick={() => setStep("first")} className="text-sm font-bold flex items-center gap-1 text-brand-ink/60"><ArrowLeft className="w-4 h-4" />{t("Terug")}</button>
              <div className="flex items-center gap-2"><Building2 className="w-5 h-5" strokeWidth={2.5} /><span className="font-head font-bold">{t("Je organisatie")}</span></div>
              <input data-testid="onb-org-name" value={org.name} onChange={(e) => setOrg({ ...org, name: e.target.value })} placeholder={t("Naam huishouden (bv. Huize Zonnehoek)")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
              <div>
                <p className="text-sm font-bold mb-1.5">{t("Wat voor organisatie is dit?")}</p>
                <div className="grid grid-cols-2 gap-2">
                  {ORG_TYPES.map((ot) => (
                    <button key={ot.v} data-testid={`onb-type-${ot.v}`} onClick={() => setOrg({ ...org, type: ot.v })}
                      className={`brutal-sm rounded-xl px-3 py-2.5 text-sm font-bold text-start ${org.type === ot.v ? "bg-brand-violet text-white" : "bg-white"}`}>{t(ot.l)}</button>
                  ))}
                </div>
              </div>

              {!user && (
                <>
                  <div className="flex items-center gap-2 pt-1"><UserPlus className="w-5 h-5" strokeWidth={2.5} /><span className="font-head font-bold">{t("Jouw account")}</span></div>
                  <input data-testid="onb-name" value={acc.name} onChange={(e) => setAcc({ ...acc, name: e.target.value })} placeholder={t("Jouw naam")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
                  <input data-testid="onb-email" type="email" value={acc.email} onChange={(e) => setAcc({ ...acc, email: e.target.value })} placeholder={t("E-mailadres")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
                  <input data-testid="onb-password" type="password" value={acc.password} onChange={(e) => setAcc({ ...acc, password: e.target.value })} placeholder={t("Wachtwoord (min. 6 tekens)")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
                  <div className="brutal-sm rounded-xl bg-brand-yellow/40 p-3 flex items-start gap-2" data-testid="onb-admin-note">
                    <Crown className="w-5 h-5 shrink-0 mt-0.5" strokeWidth={2.5} />
                    <p className="text-sm font-medium">{t("Je wordt automatisch")} <b>{t("beheerder")}</b> {t("van deze organisatie. Na je e-mailverificatie kun je meteen aan de slag.")}</p>
                  </div>
                </>
              )}

              <BrutalButton data-testid="onb-create-submit" onClick={user ? createOrg : registerOwner} className="w-full" disabled={busy}>
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Crown className="w-5 h-5" strokeWidth={2.5} />}{user ? t("Organisatie aanmaken") : t("Aanmaken & code ontvangen")}
              </BrutalButton>
            </div>
          )}

          {!checkingPending && !pendingReq && step === "verify" && (
            <div className="space-y-3 text-center" data-testid="onb-verify">
              <MailCheck className="w-12 h-12 mx-auto text-brand-violet" strokeWidth={2.5} />
              <p className="font-head font-black text-xl">{t("Check je e-mail! 📬")}</p>
              <p className="text-sm text-brand-ink/60 font-semibold -mt-1">{t("We stuurden een 6-cijferige code naar")}<br /><b>{acc.email}</b></p>
              <input data-testid="onb-verify-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => e.key === "Enter" && verify()} placeholder="000000" inputMode="numeric" maxLength={6}
                className="w-full brutal-sm rounded-xl px-4 py-3 font-head font-black text-3xl tracking-[0.4em] text-center outline-none" />
              <BrutalButton data-testid="onb-verify-submit" onClick={verify} className="w-full" disabled={busy || code.length < 6}>
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" strokeWidth={2.5} />}{t("Verifiëren & starten")}
              </BrutalButton>
              <p className="text-sm text-brand-ink/50">{t("Geen code ontvangen?")} <button data-testid="onb-verify-resend" type="button" onClick={resend} className="underline font-bold">{t("Opnieuw versturen")}</button></p>
            </div>
          )}

          {!checkingPending && !pendingReq && step === "join" && (
            <div className="space-y-3" data-testid="onb-join">
              <button onClick={() => setStep("first")} className="text-sm font-bold flex items-center gap-1 text-brand-ink/60"><ArrowLeft className="w-4 h-4" />{t("Terug")}</button>
              <div className="flex items-center gap-2"><Users className="w-5 h-5" strokeWidth={2.5} /><span className="font-head font-bold">{t("Aansluiten bij een organisatie")}</span></div>
              <p className="text-sm text-brand-ink/60 -mt-1">{t("Vraag het unieke organisatie-ID aan een lid, of laat een bestaand lid jouw QR scannen.")}</p>
              <div className="flex gap-2">
                <input data-testid="onb-join-id" value={joinId} onChange={(e) => { setJoinId(e.target.value.trim()); setJoinTarget(null); }} placeholder={t("Organisatie-ID")} className="flex-1 min-w-0 brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
                <BrutalButton data-testid="onb-join-check" onClick={() => validateOrg()} disabled={busy}>{t("Zoek")}</BrutalButton>
              </div>

              {joinTarget && (
                <div className="brutal-sm rounded-xl bg-brand-cream p-3 space-y-3" data-testid="onb-join-found">
                  <p className="font-head font-bold flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-brand-green" strokeWidth={2.5} />{joinTarget.name}</p>
                  <div className="text-center">
                    <p className="text-xs font-bold mb-2 flex items-center justify-center gap-1.5"><QrCode className="w-4 h-4" strokeWidth={2.5} />{t("Of laat een lid deze QR scannen")}</p>
                    <img src={joinQr} alt="QR" width="150" height="150" className="brutal-sm rounded-xl bg-white p-1 mx-auto" data-testid="onb-join-qr" />
                    <p className="text-[11px] text-brand-ink/60 mt-2">{t("De QR opent dit aanmeldscherm gekoppeld aan")} {joinTarget.name}.</p>
                  </div>
                  {!user && (
                    <div className="space-y-2">
                      <input data-testid="onb-contact-name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder={t("Jouw naam")} className="w-full brutal-sm rounded-xl px-3 py-2.5 text-sm outline-none" />
                      <input data-testid="onb-contact-email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder={t("Jouw e-mail")} className="w-full brutal-sm rounded-xl px-3 py-2.5 text-sm outline-none" />
                    </div>
                  )}
                  <BrutalButton data-testid="onb-join-request" onClick={requestJoin} className="w-full" disabled={busy}>
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserPlus className="w-5 h-5" strokeWidth={2.5} />}{t("Vraag goedkeuring aan")}
                  </BrutalButton>
                  <p className="text-[11px] text-brand-ink/60 text-center">{t("Een beheerder van")} {joinTarget.name} {t("keurt je aanmelding goed.")}</p>
                </div>
              )}

              <div className="brutal-sm rounded-xl bg-brand-yellow/40 p-3 flex items-start gap-2" data-testid="onb-join-tip">
                <Lightbulb className="w-5 h-5 shrink-0 mt-0.5" strokeWidth={2.5} />
                <p className="text-sm font-medium">{t("Tip: contacteer de beheerder van je organisatie, die kan je helpen met de code.")}</p>
              </div>
              {!user && <p className="text-center text-sm text-brand-ink/50">{t("Al een account?")} <button type="button" onClick={() => navigate("/login")} className="underline font-bold">{t("Inloggen")}</button></p>}
              <button data-testid="onb-join-back-landing" onClick={() => navigate("/")}
                className="w-full text-sm font-bold text-brand-ink/60 underline py-1">{t("Ik weet het ID nu niet")}</button>
            </div>
          )}

          {!checkingPending && !pendingReq && step === "waiting" && (
            <div className="text-center py-6" data-testid="onb-waiting">
              <CheckCircle2 className="w-14 h-14 mx-auto text-brand-green mb-3" strokeWidth={2.5} />
              <h2 className="font-head font-black text-xl">{t("Aanvraag verstuurd!")}</h2>
              <p className="text-brand-ink/60 font-semibold mt-1">{t("Een beheerder bekijkt je aanmelding. Je krijgt bericht zodra je bent goedgekeurd.")}</p>
              {user && <BrutalButton onClick={() => navigate("/vandaag")} className="mt-4">{t("Naar Slackr")}</BrutalButton>}
            </div>
          )}
        </BrutalCard>
      </div>
    </div>
  );
}
